import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import type { BacklogItem } from "@/types/backlog";
import { normalizeBacklogItem } from "@/lib/backlogUtils";
import { fetchAllRows } from "@/lib/fetchAllRows";

export const useBacklogItems = () => {
  return useQuery<BacklogItem[]>({
    queryKey: ["backlog-items"],
    queryFn: async () => {
      // Page through all rows: an unbounded select is silently capped at
      // PostgREST's default 1000-row limit, which would drop newly-created
      // items that sort to the tail (their per-subsheet row_number lands near
      // the global max) and hide them from every list view.
      const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
        supabase
          .from("backlog_items")
          .select("*")
          .is("deleted_at", null)
          .order("row_number", { ascending: true })
          // Tiebreaker — row_number is assigned per-subsheet and is not
          // globally unique, so a unique secondary sort is required for
          // stable page boundaries (otherwise rows can be skipped/duplicated).
          .order("id", { ascending: true })
          .range(from, to),
      );
      return rows.map(normalizeBacklogItem);
    },
    staleTime: Infinity, // Realtime keeps it fresh
    gcTime: 24 * 60 * 60 * 1000, // 24 hours cache
  });
};

export const useUpdateBacklogItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Not custom_fields: values change one key at a time through
    // set_ticket_custom_field (useSetTicketCustomField), never by overwriting
    // the whole object.
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Omit<BacklogItem, "custom_fields">> }) => {
      const { data, error } = await supabase.from("backlog_items").update(updates).eq("id", id).select().single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Realtime handles the update, but we can still invalidate for consistency
      queryClient.invalidateQueries({ queryKey: ["backlog-items"] });
    },
  });
};

export const useInvalidateBacklogItems = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ["backlog-items"] });
  };
};

export type { BacklogItem };
